import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Icon } from "@/components/Icon";
import { cn } from "@/lib/utils";

/* CategoryCards — the headline BOLD-UI change (design-ref/BOLD_UI_DIRECTION.md §3).

   Replaces the flat text pill row (SegmentedPills) in the Casino LOBBY ONLY with big, tappable
   ILLUSTRATED category cards: a .surface-premium panel with a large watermark glyph, a foreground
   glyph chip, the name (Manrope 800) and a tabular-nums count. Categories differ by LIGHT ANGLE +
   ICONOGRAPHY (+ a functional live-red hairline for Live Dealers) — never by an arbitrary hue, so
   the set stays cohesive charcoal with green reserved for the genuine selected state.

   Consumes the SAME value/onChange contract as SegmentedPills so the Casino.jsx swap is one line:
   - items: [{ id, label, icon, count? }]
   - value: selected id
   - onChange(id)
   - testidPrefix (default "cat"): each card is `${testidPrefix}-${id}`

   Mobile: horizontal-scroll row of 128px cards (scroll-snap). Desktop (lg): a 5-col grid. */

// Per-category gradient light-angle (§3.2) — stays as the FALLBACK base behind the artwork.
const CAT_GRADIENT = {
  lobby: "145deg",     // default lit panel
  originals: "115deg", // steeper, more vertical light
  slots: "170deg",     // near-horizontal sheen
  live: "135deg",      // + a live-red bottom hairline (below)
  table: "160deg",
};

// Self-hosted custom category artwork (§3b) — one cohesive dark/charcoal + green-signature set,
// served same-origin from /public/categories/. Falls back to the gradient+glyph if an image 404s.
const CAT_ART = {
  lobby: "/categories/lobby.webp",
  originals: "/categories/originals.webp",
  slots: "/categories/slots.webp",
  live: "/categories/live.webp",
  table: "/categories/table.webp",
};

export default function CategoryCards({
  items = [],
  value,
  onChange,
  testidPrefix = "cat",
  className,
  ariaLabel = "Game category",
}) {
  const reduce = useReducedMotion();
  // Quick one-shot entrance stagger (§7), guarded by reduced-motion.
  const container = {
    hidden: {},
    show: { transition: reduce ? {} : { staggerChildren: 0.04, delayChildren: 0.02 } },
  };
  const item = reduce
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 10 },
        show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
      };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "no-scrollbar flex gap-3 overflow-x-auto pb-1 lg:grid lg:grid-cols-5 lg:gap-3 lg:overflow-visible lg:pb-0",
        className
      )}
      style={{ scrollSnapType: "x proximity" }}
    >
      {items.map((cat) => {
        const on = value === cat.id;
        const angle = CAT_GRADIENT[cat.id] || "145deg";
        return (
          <motion.button
            key={cat.id}
            type="button"
            role="tab"
            aria-selected={on}
            data-on={on}
            data-testid={`${testidPrefix}-${cat.id}`}
            onClick={() => onChange && onChange(cat.id)}
            variants={item}
            whileTap={reduce ? undefined : { scale: 0.98 }}
            style={{
              scrollSnapAlign: "start",
              backgroundImage: `linear-gradient(${angle}, hsl(var(--ink-700)) 0%, hsl(var(--surface-3)) 45%, hsl(var(--ink-800)) 100%)`,
            }}
            className={cn(
              "surface-premium group relative flex h-[108px] w-[128px] shrink-0 flex-col justify-between overflow-hidden rounded-2xl p-3 text-left transition-all duration-[var(--dur-base)] focus-ring",
              "lg:h-[120px] lg:w-full",
              "hover:-translate-y-0.5 hover:shadow-[var(--elev-3)]",
              on ? "shadow-[var(--glow-green-edge),var(--elev-3)]" : "",
              // Live Dealers carries a faint functional-red "on-air" hairline (§3.2).
              cat.id === "live" ? "shadow-[inset_0_-2px_0_rgba(239,68,68,0.35),var(--elev-2)]" : ""
            )}
          >
            {/* Custom category artwork (§3b) — full-bleed behind the content; a bottom+left scrim keeps
                the glyph chip + title legible. onError hides it so the gradient+glyph remains the fallback. */}
            {CAT_ART[cat.id] && (
              <img
                src={CAT_ART[cat.id]}
                alt=""
                aria-hidden="true"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-90 transition-transform duration-[var(--dur-xslow)] group-hover:scale-105"
              />
            )}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/55 to-transparent" />
            {/* foreground glyph chip */}
            <span
              className={cn(
                "relative flex h-10 w-10 items-center justify-center rounded-xl ring-1 backdrop-blur-sm transition-colors",
                on
                  ? "bg-pnr-green/20 ring-pnr-green/50"
                  : "bg-black/40 ring-white/15"
              )}
            >
              <Icon
                name={cat.icon}
                size={22}
                className={on ? "text-pnr-green" : "text-white"}
              />
            </span>
            <div className="relative">
              <p className="font-display text-[15px] font-black leading-tight tracking-tight text-white drop-shadow-[0_1px_5px_rgba(0,0,0,0.9)]">
                {cat.label}
              </p>
              {cat.count != null && (
                <p
                  className={cn(
                    "mt-0.5 text-[11px] font-bold tabular-nums drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]",
                    on ? "text-pnr-green" : "text-white/75"
                  )}
                >
                  {cat.count} games
                </p>
              )}
            </div>
          </motion.button>
        );
      })}
    </motion.div>
  );
}
